/**
 * Content Store
 * Manages content module UI state
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type LayoutType = 'table' | 'cards' | 'calendar' | 'kanban' | 'map';

export interface Bookmark {
    id: string;
    name: string;
    icon?: string;
    color?: string;
    collection: string;
    filter?: Record<string, unknown>;
    search?: string;
    layout?: LayoutType;
    layoutOptions?: Record<string, unknown>;
}

export type ArchiveFilter = 'active' | 'archived' | 'all';

interface ContentState {
    // Right sidebar
    rightSidebarOpen: boolean;
    setRightSidebarOpen: (open: boolean) => void;
    toggleRightSidebar: () => void;

    // Active sidebar section
    activeSidebarSection: string | null;
    setActiveSidebarSection: (section: string | null) => void;

    // Layout per collection
    layouts: Record<string, LayoutType>;
    setLayout: (collection: string, layout: LayoutType) => void;
    getLayout: (collection: string) => LayoutType;

    // Layout options per collection
    layoutOptions: Record<string, Record<string, unknown>>;
    setLayoutOptions: (collection: string, options: Record<string, unknown>) => void;
    getLayoutOptions: (collection: string) => Record<string, unknown>;

    // Archive filter per collection
    archiveFilters: Record<string, ArchiveFilter>;
    setArchiveFilter: (collection: string, filter: ArchiveFilter) => void;
    getArchiveFilter: (collection: string) => ArchiveFilter;

    // Bookmarks
    bookmarks: Bookmark[];
    addBookmark: (bookmark: Bookmark) => void;
    updateBookmark: (id: string, updates: Partial<Bookmark>) => void;
    removeBookmark: (id: string) => void;
    getBookmarksForCollection: (collection: string) => Bookmark[];

    // Active bookmark
    activeBookmarkId: number | null;
    setActiveBookmark: (id: number | null) => void;

    // Refresh interval per collection (in seconds)
    refreshIntervals: Record<string, number | null>;
    setRefreshInterval: (collection: string, interval: number | null) => void;
    getRefreshInterval: (collection: string) => number | null;

    // Navigation groups (expanded/collapsed)
    expandedGroups: string[];
    toggleGroup: (groupId: string) => void;
    isGroupExpanded: (groupId: string) => boolean;
}

export const useContentStore = create<ContentState>()(
    persist(
        (set, get) => ({
            // Right sidebar - default open
            rightSidebarOpen: true,
            setRightSidebarOpen: (open) => set({ rightSidebarOpen: open }),
            toggleRightSidebar: () => set((state) => ({ rightSidebarOpen: !state.rightSidebarOpen })),

            // Active sidebar section
            activeSidebarSection: 'info',
            setActiveSidebarSection: (section) => set({ activeSidebarSection: section }),

            // Layout per collection
            layouts: {},
            setLayout: (collection, layout) =>
                set((state) => ({
                    layouts: { ...state.layouts, [collection]: layout },
                })),
            getLayout: (collection) => get().layouts[collection] || 'table',

            // Layout options
            layoutOptions: {},
            setLayoutOptions: (collection, options) =>
                set((state) => ({
                    layoutOptions: { ...state.layoutOptions, [collection]: options },
                })),
            getLayoutOptions: (collection) => get().layoutOptions[collection] || {},

            // Archive filter
            archiveFilters: {},
            setArchiveFilter: (collection, filter) =>
                set((state) => ({
                    archiveFilters: { ...state.archiveFilters, [collection]: filter },
                })),
            getArchiveFilter: (collection) => get().archiveFilters[collection] || 'active',

            // Bookmarks
            bookmarks: [],
            addBookmark: (bookmark) =>
                set((state) => ({
                    bookmarks: [...state.bookmarks, bookmark],
                })),
            updateBookmark: (id, updates) =>
                set((state) => ({
                    bookmarks: state.bookmarks.map((b) =>
                        b.id === id ? { ...b, ...updates } : b
                    ),
                })),
            removeBookmark: (id) =>
                set((state) => ({
                    bookmarks: state.bookmarks.filter((b) => b.id !== id),
                })),
            getBookmarksForCollection: (collection) =>
                get().bookmarks.filter((b) => b.collection === collection),

            // Active bookmark
            activeBookmarkId: null,
            setActiveBookmark: (id) => set({ activeBookmarkId: id }),

            // Refresh intervals
            refreshIntervals: {},
            setRefreshInterval: (collection, interval) =>
                set((state) => ({
                    refreshIntervals: { ...state.refreshIntervals, [collection]: interval },
                })),
            getRefreshInterval: (collection) => get().refreshIntervals[collection] ?? null,

            // Navigation groups
            expandedGroups: [],
            toggleGroup: (groupId) =>
                set((state) => ({
                    expandedGroups: state.expandedGroups.includes(groupId)
                        ? state.expandedGroups.filter((id) => id !== groupId)
                        : [...state.expandedGroups, groupId],
                })),
            isGroupExpanded: (groupId) => get().expandedGroups.includes(groupId),
        }),
        {
            name: 'content-store',
            partialize: (state) => ({
                layouts: state.layouts,
                layoutOptions: state.layoutOptions,
                archiveFilters: state.archiveFilters,
                bookmarks: state.bookmarks,
                refreshIntervals: state.refreshIntervals,
                expandedGroups: state.expandedGroups,
                rightSidebarOpen: state.rightSidebarOpen,
            }),
        }
    )
);
